import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { 
  Plus, Search, Filter, Calendar, Users, CheckCircle, XCircle,
  Clock, AlertCircle, Utensils, MapPin, DollarSign, FileText,
  Eye, Edit, Trash2, Send, RefreshCw, AlertTriangle, MoreVertical
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Input, Label, Select, Textarea } from '../../components/ui/Form';
import { Badge } from '../../components/ui/Badge';
import { useToast } from '../../contexts/ToastContext';
import { mealRequestsAPI, lookupDataAPI } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';

const mealRequestSchema = z.object({
  schoolId: z.string().optional(),
  branchId: z.string().optional(),
  mealDate: z.string().min(1, 'Meal date is required'),
  classCounts: z.record(z.string(), z.number().min(0)).refine(
    (counts) => Object.values(counts).some(count => count > 0),
    'At least one class must have students'
  ),
  specialRequirements: z.string().optional(),
  notes: z.string().optional(),
});

const MealRequestManagement = () => {
  const { toast } = useToast();
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [requests, setRequests] = useState([]);
  const [schools, setSchools] = useState([]);
  const [branches, setBranches] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [refreshing, setRefreshing] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState({ show: false, requestId: null, requestTitle: '' });
  const [editingRequest, setEditingRequest] = useState(null);
  const [showStatusDropdown, setShowStatusDropdown] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedRequestForDetails, setSelectedRequestForDetails] = useState(null);

  const defaultValues = {
    schoolId: user?.school_id || '',
    branchId: user?.branch_id || '',
    mealDate: new Date().toISOString().split('T')[0],
    classCounts: {
      ECD: 0,
      '1': 0,
      '2': 0,
      '3': 0,
      '4': 0,
      '5': 0,
      '6': 0,
      '7': 0,
      '8': 0,
      '9': 0,
      '10': 0,
    },
    specialRequirements: '',
    notes: '',
  };

  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
    watch,
  } = useForm({
    resolver: zodResolver(mealRequestSchema),
    defaultValues,
  });

  const selectedBranch = watch('branchId');
  const selectedSchool = watch('schoolId');
  
  // Determine if user has an associated school
  const hasAssociatedSchool = !!user?.school_id;
  const shouldShowSchoolDropdown = !hasAssociatedSchool;

  useEffect(() => {
    loadInitialData();
  }, []);

  useEffect(() => {
    if (selectedBranch) {
      loadSchools(selectedBranch);
    }
  }, [selectedBranch]);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('ne-NP', {
      style: 'currency',
      currency: 'NPR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const loadInitialData = async () => {
    try {
      const [branchesData, requestsData] = await Promise.all([
        lookupDataAPI.getBranches(),
        mealRequestsAPI.getRequests()
      ]);
      
      setBranches((branchesData.branches || []).filter(branch => branch.is_active));
      // The API returns an array directly, not wrapped in a requests property
      setRequests(Array.isArray(requestsData) ? requestsData : []);
      
      // Set user's branch as default if available
      if (user?.branch_id) {
        reset({ ...watch(), branchId: user.branch_id });
      }
    } catch (error) {
      console.error('Failed to load initial data:', error);
      toast.error('Failed to load data', 'Please try again');
    }
  };

  const loadSchools = async (branchId) => {
    try {
      const schoolsData = await lookupDataAPI.getSchools(branchId);
      setSchools(schoolsData.schools || []);
    } catch (error) {
      console.error('Failed to load schools:', error);
      setSchools([]);
    }
  };

  const loadRequests = async () => {
    try {
      const requestsData = await mealRequestsAPI.getRequests();
      // The API returns an array, not an object with requests property
      setRequests(Array.isArray(requestsData) ? requestsData : []);
    } catch (error) {
      console.error('Failed to load requests:', error);
      toast.error('Failed to load requests');
    }
  };

  const refreshData = async () => {
    setRefreshing(true);
    try {
      await Promise.all([
        loadRequests(),
        loadSchools(selectedBranch)
      ]);
      toast.success('Data refreshed successfully');
    } catch (error) {
      toast.error('Failed to refresh data');
    } finally {
      setRefreshing(false);
    }
  };

  const onSubmit = async (data) => {
    setLoading(true);
    try {
      const requestData = {
        school_id: data.schoolId,
        date: data.mealDate,
        class_counts: data.classCounts,
        remarks: `${data.notes || ''} ${data.specialRequirements ? `Special: ${data.specialRequirements}` : ''}`.trim(),
      };

      let result;
      if (editingRequest) {
        // Update existing request
        result = await mealRequestsAPI.updateRequest(editingRequest.id, requestData);
        toast.success('Meal request updated successfully!', `Request ID: ${editingRequest.id}`);
      } else {
        // Create new request
        result = await mealRequestsAPI.createRequest(requestData);
        toast.success('Meal request submitted successfully!', `Request ID: ${result.id}`);
      }
      
      setShowForm(false);
      setEditingRequest(null);
      reset();
      await loadRequests();
    } catch (error) {
      const errorMessage = error.response?.data?.detail || 'Failed to submit request';
      toast.error('Failed to submit request', errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (requestId) => {
    try {
      await mealRequestsAPI.approveRequest(requestId);
      toast.success('Request approved!', 'Meal request has been approved');
      await loadRequests();
    } catch (error) {
      const errorMessage = error.response?.data?.detail || 'Failed to approve request';
      toast.error('Failed to approve request', errorMessage);
    }
  };

  const handleReject = async (requestId) => {
    const reason = prompt('Please provide rejection reason:');
    if (reason) {
      try {
        await mealRequestsAPI.rejectRequest(requestId, reason);
        toast.success('Request rejected!', 'Meal request has been rejected');
        await loadRequests();
      } catch (error) {
        const errorMessage = error.response?.data?.detail || 'Failed to reject request';
        toast.error('Failed to reject request', errorMessage);
      }
    }
  };

  const handleStatusChange = async (requestId, newStatus) => {
    try {
      await mealRequestsAPI.updateRequest(requestId, { status: newStatus });
      toast.success('Status updated successfully!', `Request status changed to ${newStatus}`);
      await loadRequests();
      setShowStatusDropdown(null);
    } catch (error) {
      const errorMessage = error.response?.data?.detail || 'Failed to update status';
      toast.error('Failed to update status', errorMessage);
    }
  };

  const confirmDelete = async () => {
    try {
      await mealRequestsAPI.updateRequest(deleteConfirm.requestId, { status: 'cancelled' });
      toast.success('Request cancelled successfully!');
      await loadRequests();
      setDeleteConfirm({ show: false, requestId: null, requestTitle: '' });
    } catch (error) {
      const errorMessage = error.response?.data?.detail || 'Failed to cancel request';
      toast.error('Failed to cancel request', errorMessage);
    }
  };

  const handleEdit = (request) => {
    setEditingRequest(request);
    // Pre-fill the form with existing data
    reset({
      schoolId: request.school_id,
      branchId: user?.branch_id || '',
      mealDate: request.date,
      classCounts: { ...defaultValues.classCounts, ...(request.class_counts || {}) },
      specialRequirements: request.remarks || '',
      notes: '',
    });
    setShowForm(true);
  };

  const handleAddNew = () => {
    setEditingRequest(null);
    reset(defaultValues);
    setShowForm(true);
  };

  const handleShowDetails = (request) => {
    setSelectedRequestForDetails(request);
    setShowDetailsModal(true);
  };

  const closeDetailsModal = () => {
    setShowDetailsModal(false);
    setSelectedRequestForDetails(null);
  };

  const handleDelete = (request) => {
    setDeleteConfirm({ show: true, requestId: request.id, requestTitle: `${request.school_name || 'Unknown School'} - ${request.total_meals || 0} meals` });
  };

  const cancelDelete = () => {
    setDeleteConfirm({ show: false, requestId: null, requestTitle: '' });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'delivered': 
      case 'acknowledged': 
      case 'approved': return 'bg-green-100 text-green-700 border-green-200';
      case 'cancelled': 
      case 'rejected': return 'bg-red-100 text-red-700 border-red-200';
      case 'order_received': 
      case 'preparing': 
      case 'ready_for_delivery': 
      case 'in_transit': 
      case 'pending': return 'bg-amber-100 text-amber-700 border-amber-200';
      default: return 'bg-gray-100 text-gray-700 border-gray-200';
    }
  };

  const getMealTypeLabel = (type) => {
    switch (type) {
      case 'breakfast': return 'Breakfast';
      case 'lunch': return 'Lunch';
      case 'dinner': return 'Dinner';
      case 'snacks': return 'Snacks';
      default: return type;
    }
  };

  const filteredRequests = requests.filter(request => {
    const matchesSearch = request.school_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         request.school_code?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         request.requested_by?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = filterStatus === 'all' || request.status === filterStatus;
    return matchesSearch && matchesStatus;
  });

  const requestStats = {
    total: filteredRequests.length,
    pending: filteredRequests.filter(r => r.status === 'order_received' || r.status === 'pending').length,
    approved: filteredRequests.filter(r => r.status === 'acknowledged' || r.status === 'approved' || r.status === 'delivered').length,
    rejected: filteredRequests.filter(r => r.status === 'cancelled' || r.status === 'rejected').length,
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Meal Request Management</h1>
            <p className="text-gray-600 mt-2">Manage and track meal requests from schools</p>
          </div>
          <div className="mt-4 sm:mt-0 flex items-center space-x-3">
            <Button onClick={handleAddNew}>
              <Plus size={16} className="mr-2" />
              New Request
            </Button>
            <Button variant="outline" size="sm">
              <RefreshCw size={16} className="mr-2" />
              Refresh
            </Button>
          </div>
        </div>
      </motion.div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {[
          { label: 'Total Requests', value: requestStats.total, color: 'blue', icon: FileText },
          { label: 'Pending', value: requestStats.pending, color: 'amber', icon: Clock },
          { label: 'Approved', value: requestStats.approved, color: 'green', icon: CheckCircle },
        ].map((stat, index) => (
          <motion.div
            key={stat.label}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: index * 0.1 }}
          >
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className={`text-2xl font-bold text-${stat.color}-600`}>{stat.value}</p>
                    <p className="text-sm text-gray-600 mt-1">{stat.label}</p>
                  </div>
                  <div className={`w-10 h-10 bg-${stat.color}-100 rounded-lg flex items-center justify-center`}>
                    <stat.icon className={`text-${stat.color}-600`} size={20} />
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Request Form */}
        {showForm && (
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5 }}
            className="lg:col-span-1"
          >
            <Card>
              <CardHeader>
                <CardTitle>{editingRequest ? 'Edit Meal Request' : 'New Meal Request'}</CardTitle>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
                  {/* Branch Field - Auto-mapped from user, disabled */}
                  <div>
                    <Label htmlFor="branchId">Branch</Label>
                    <Controller
                      name="branchId"
                      control={control}
                      render={({ field }) => (
                        <Select 
                          {...field} 
                          disabled
                          error={errors.branchId?.message}
                        >
                          <option value="">{user?.branch_name || 'Auto-mapped from your account'}</option>
                        </Select>
                      )}
                    />
                    <p className="text-xs text-gray-500 mt-1">Automatically set from your account</p>
                  </div>

                  {/* School Field - Only show if user doesn't have school_id */}
                  {shouldShowSchoolDropdown && (
                    <div>
                      <Label htmlFor="schoolId">School</Label>
                      <Controller
                        name="schoolId"
                        control={control}
                        render={({ field }) => (
                          <Select {...field} error={errors.schoolId?.message}>
                            <option value="">Select School</option>
                            {schools.map(school => (
                              <option key={school.id} value={school.id}>
                                {school.name} ({school.students} students)
                              </option>
                            ))}
                          </Select>
                        )}
                      />
                    </div>
                  )}

                  {!shouldShowSchoolDropdown && user?.school_name && (
                    <div>
                      <Label>School</Label>
                      <div className="w-full p-3 bg-gray-50 rounded-lg border border-gray-200">
                        <p className="text-sm font-medium">{user.school_name}</p>
                        <p className="text-xs text-gray-500 mt-1">Automatically set from your account</p>
                      </div>
                    </div>
                  )}

                  <div>
                    <Label htmlFor="mealDate">Meal Date</Label>
                    <Controller
                      name="mealDate"
                      control={control}
                      render={({ field }) => (
                        <Input
                          type="date"
                          {...field}
                          error={errors.mealDate?.message}
                        />
                      )}
                    />
                  </div>

                  <div>
                    <Label>Student Counts by Class</Label>
                    <div className="grid grid-cols-2 gap-3 mt-2">
                      {Object.keys(defaultValues.classCounts).map((className) => (
                        <div key={className}>
                          <Label htmlFor={`classCounts.${className}`} className="text-sm">
                            Class {className}
                          </Label>
                          <Controller
                            name={`classCounts.${className}`}
                            control={control}
                            render={({ field }) => (
                              <Input
                                type="number"
                                min="0"
                                placeholder="0"
                                {...field}
                                onChange={(e) => field.onChange(parseInt(e.target.value) || 0)}
                              />
                            )}
                          />
                        </div>
                      ))}
                    </div>
                    {errors.classCounts && (
                      <p className="text-sm text-red-600 mt-1">{errors.classCounts.message}</p>
                    )}
                  </div>

                  <div>
                    <Label htmlFor="specialRequirements">Special Requirements</Label>
                    <Controller
                      name="specialRequirements"
                      control={control}
                      render={({ field }) => (
                        <Textarea
                          placeholder="Any special dietary requirements..."
                          {...field}
                        />
                      )}
                    />
                  </div>

                  <div>
                    <Label htmlFor="notes">Notes</Label>
                    <Controller
                      name="notes"
                      control={control}
                      render={({ field }) => (
                        <Textarea
                          placeholder="Additional notes..."
                          {...field}
                        />
                      )}
                    />
                  </div>

                  <div className="flex space-x-3">
                    <Button
                      type="submit"
                      className="flex-1"
                      loading={loading}
                    >
                      Submit Request
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => {
                        setShowForm(false);
                        setEditingRequest(null);
                        reset();
                      }}
                    >
                      Cancel
                    </Button>
                  </div>
                </form>
              </CardContent>
            </Card>
          </motion.div>
        )}

        {/* Requests List */}
        <motion.div
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}
          className={`${showForm ? 'lg:col-span-2' : 'lg:col-span-3'}`}
        >
          <Card>
            <CardHeader>
              <CardTitle>Meal Requests</CardTitle>
              <div className="flex flex-col sm:flex-row gap-3 mt-4">
                <div className="flex-1">
                  <Input
                    placeholder="Search requests..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10"
                  />
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
                </div>
                <Select
                  value={filterStatus}
                  onChange={(e) => setFilterStatus(e.target.value)}
                  className="sm:w-40"
                >
                  <option value="all">All Status</option>
                  <option value="order_received">Order Received</option>
                  <option value="acknowledged">Acknowledged</option>
                  <option value="preparing">Preparing</option>
                  <option value="ready_for_delivery">Ready for Delivery</option>
                  <option value="in_transit">In Transit</option>
                  <option value="delivered">Delivered</option>
                  <option value="cancelled">Cancelled</option>
                </Select>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-3 max-h-96 overflow-y-auto">
                {filteredRequests.length === 0 ? (
                  <div className="text-center py-8">
                    <Utensils className="text-gray-400 mx-auto mb-2" size={48} />
                    <p className="text-gray-500">No meal requests found</p>
                  </div>
                ) : (
                  filteredRequests.map((request, index) => {
                    return (
                    <motion.div
                      key={request.id}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.3, delay: 0.3 + index * 0.05 }}
                      className="border border-gray-100 rounded-lg p-4 hover:bg-gray-50"
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center space-x-2 mb-2">
                            <h3 className="font-semibold text-gray-900">
                              {request.school_name || 'Unknown School'}
                            </h3>
                            <Badge className={getStatusColor(request.status || 'order_received')}>
                              {request.status || 'order_received'}
                            </Badge>
                          </div>
                          
                          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 text-sm text-gray-600">
                            <div className="flex items-center">
                              <MapPin size={14} className="mr-1" />
                              {request.school_code || 'N/A'}
                            </div>
                            <div className="flex items-center">
                              <Calendar size={14} className="mr-1" />
                              {request.date}
                            </div>
                            <div className="flex items-center">
                              <Utensils size={14} className="mr-1" />
                              {request.total_meals || 0} meals
                            </div>
                          </div>

                          {request.remarks && (
                            <p className="text-sm text-gray-600 mt-2">
                              <strong>Notes:</strong> {request.remarks}
                            </p>
                          )}

                          <div className="flex items-center justify-between mt-3 text-xs text-gray-500">
                            <span>Requested by {user?.full_name || request.requested_by || 'You'} • {new Date(request.created_at).toLocaleString()}</span>
                            <Button size="sm" variant="outline" onClick={() => handleShowDetails(request)}>
                              <Eye size={14} className="mr-1" />
                              Details
                            </Button>
                          </div>
                        </div>

                        <div className="flex items-center space-x-2 ml-4">
                          <div className="relative">
                            <Button 
                              size="sm" 
                              variant="ghost" 
                              onClick={() => setShowStatusDropdown(showStatusDropdown === request.id ? null : request.id)}
                            >
                              <MoreVertical size={14} />
                            </Button>
                            
                            {showStatusDropdown === request.id && (
                              <motion.div
                                initial={{ opacity: 0, y: -10 }}
                                animate={{ opacity: 1, y: 0 }}
                                className="absolute right-0 top-8 bg-white border border-gray-200 rounded-lg shadow-lg z-10 min-w-40"
                              >
                                <div className="py-1">
                                  <button
                                    className="w-full text-left px-4 py-2 text-sm hover:bg-gray-100 flex items-center"
                                    onClick={() => handleStatusChange(request.id, 'order_received')}
                                  >
                                    <Clock size={14} className="mr-2" />
                                    Mark as Order Received
                                  </button>
                                  <button
                                    className="w-full text-left px-4 py-2 text-sm hover:bg-gray-100 flex items-center"
                                    onClick={() => handleStatusChange(request.id, 'acknowledged')}
                                  >
                                    <CheckCircle size={14} className="mr-2" />
                                    Acknowledge
                                  </button>
                                  <button
                                    className="w-full text-left px-4 py-2 text-sm hover:bg-gray-100 flex items-center"
                                    onClick={() => handleStatusChange(request.id, 'preparing')}
                                  >
                                    <Utensils size={14} className="mr-2" />
                                    Preparing
                                  </button>
                                  <button
                                    className="w-full text-left px-4 py-2 text-sm hover:bg-gray-100 flex items-center"
                                    onClick={() => handleStatusChange(request.id, 'delivered')}
                                  >
                                    <CheckCircle size={14} className="mr-2" />
                                    Mark as Delivered
                                  </button>
                                  <div className="border-t border-gray-100 my-1"></div>
                                  <button
                                    className="w-full text-left px-4 py-2 text-sm hover:bg-gray-100 flex items-center text-red-600"
                                    onClick={() => handleStatusChange(request.id, 'cancelled')}
                                  >
                                    <XCircle size={14} className="mr-2" />
                                    Cancel Request
                                  </button>
                                </div>
                              </motion.div>
                            )}
                          </div>
                          <Button size="sm" variant="ghost" onClick={() => handleEdit(request)}>
                            <Edit size={14} />
                          </Button>
                          <Button size="sm" variant="ghost">
                            <Eye size={14} />
                          </Button>
                          <Button size="sm" variant="ghost" onClick={() => handleDelete(request)}>
                            <Trash2 size={14} />
                          </Button>
                        </div>
                      </div>
                    </motion.div>
                    );
                  })
                )}
              </div>
            </CardContent>
          </Card>
        </motion.div>
      </div>

      {/* Delete Confirmation Modal */}
      {deleteConfirm.show && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
          onClick={cancelDelete}
        >
          <motion.div
            initial={{ scale: 0.9 }}
            animate={{ scale: 1 }}
            className="bg-white rounded-lg p-6 max-w-md w-full mx-4"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center mb-4">
              <AlertTriangle className="text-red-600 mr-3" size={24} />
              <h3 className="text-lg font-bold text-gray-900">Cancel Meal Request</h3>
            </div>
            
            <div className="mb-6">
              <p className="text-gray-600">
                Are you sure you want to cancel the meal request <strong>"{deleteConfirm.requestTitle}"</strong>?
              </p>
              <p className="text-sm text-gray-500 mt-2">
                This action cannot be undone and will mark the request as cancelled.
              </p>
            </div>
            
            <div className="flex space-x-3">
              <Button
                variant="outline"
                onClick={cancelDelete}
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                onClick={confirmDelete}
                className="flex-1 bg-red-600 hover:bg-red-700"
              >
                Cancel Request
              </Button>
            </div>
          </motion.div>
        </motion.div>
      )}

      {/* Details Modal */}
      {showDetailsModal && selectedRequestForDetails && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
          onClick={closeDetailsModal}
        >
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            className="bg-white rounded-lg p-6 max-w-md w-full mx-4"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">Meal Request Details</h3>
              <Button variant="ghost" size="sm" onClick={closeDetailsModal}>
                <XCircle size={20} />
              </Button>
            </div>
            
            <div className="space-y-3">
              <div>
                <p className="font-medium">{selectedRequestForDetails.school_name}</p>
                <p className="text-sm text-gray-600">{selectedRequestForDetails.date}</p>
              </div>
              
              <div>
                <p className="font-medium mb-2">Student Counts by Class:</p>
                <div className="grid grid-cols-2 gap-2">
                  {Object.entries(selectedRequestForDetails.class_counts || {}).map(([className, count]) => (
                    <div key={className} className="flex justify-between">
                      <span>Class {className}:</span>
                      <span>{count}</span>
                    </div>
                  ))}
                </div>
              </div>
              
              <div className="border-t pt-3">
                <div className="flex justify-between font-semibold">
                  <span>Total Meals:</span>
                  <span>{selectedRequestForDetails.total_meals}</span>
                </div>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </div>
  );
};

export default MealRequestManagement;
